export class FelixChessMoveList {
    constructor(options = {}) {
        this.rootEl  = options.rootEl  || null;
        this.listEl  = options.listEl  || null;
        this.replay  = options.replay  || null;
        this.context = options.context || 'default';
        this.layout  = options.layout  || 'horizontal';

        // Variation children for the current position (tree-context only).
        this._variations = [];
        this._onVariationClick = null;
        this._variationController = null;
        this._unsubscribeSidecarVariations = null;
        this._lastState = null;

        this.unsubscribeGameLoaded     = null;
        this.unsubscribePositionChange = null;
        this.unsubscribeCleared        = null;

        this.onClick = this.onClick.bind(this);
        this.renderShell();
        this.bindEvents();
        this.bindReplay();
    }

    // ─── Public API ────────────────────────────────────────────────────────

    /**
     * Supply variation children for the current position.
     * Called by FelixChessBoardController.loadTreePath().
     * @param {Array}    variations       [{san, fen, games}]
     * @param {Function} onVariationClick callback(item) on click
     */
    setVariations(variations, onVariationClick) {
        this._variations       = Array.isArray(variations) ? variations : [];
        this._onVariationClick = typeof onVariationClick === 'function' ? onVariationClick : null;
        this._renderVariations();
    }

    clearVariations() {
        this._variations       = [];
        this._onVariationClick = null;
        this._renderVariations();
    }

    setVariationController(controller) {
        if (this._unsubscribeSidecarVariations) {
            this._unsubscribeSidecarVariations();
            this._unsubscribeSidecarVariations = null;
        }

        this._variationController = controller || null;
        if (this._variationController && typeof this._variationController.onChange === 'function') {
            this._unsubscribeSidecarVariations = this._variationController.onChange(() => {
                this.refreshVariationRows();
            });
        }
        this.refreshVariationRows();
    }

    refreshVariationRows() {
        if (!this._lastState || !this._lastState.hasGame) {
            return;
        }
        this.renderMoves(this._lastState);
        this.updateFromState(this._lastState);
    }

    // ─── Shell ─────────────────────────────────────────────────────────────

    renderShell() {
        if (!this.listEl) return;
        this.listEl.innerHTML = `
            <div class="fc-boardViewer__moveListInner" data-role="moveListInner"></div>
            <div class="fc-boardViewer__variations"    data-role="variationsBlock" hidden></div>
        `;
        this.listEl.hidden = true;
        this.listEl.setAttribute('data-layout', this.getNormalizedLayout());
    }

    // ─── Events ────────────────────────────────────────────────────────────

    bindEvents() {
        if (!this.listEl) return;
        this.listEl.addEventListener('click', this.onClick);
    }

    bindReplay() {
        if (!this.replay) return;
        this.unsubscribeGameLoaded = this.replay.on('gameloaded', (state) => {
            this.renderMoves(state);
            this.updateFromState(state);
        });
        this.unsubscribePositionChange = this.replay.on('positionchange', (state) => {
            this.updateFromState(state);
        });
        this.unsubscribeCleared = this.replay.on('cleared', () => {
            this.clear();
        });
    }

    onClick(e) {
        // Variation button click
        const varBtn = e.target.closest('[data-variation-index]');
        if (varBtn) {
            const idx = parseInt(varBtn.getAttribute('data-variation-index'), 10);
            if (!Number.isNaN(idx) && this._variations[idx] && this._onVariationClick) {
                this._onVariationClick(this._variations[idx]);
            }
            return;
        }

        const sidecarMove = e.target.closest('[data-sidecar-variation-id][data-sidecar-move-index]');
        if (sidecarMove) {
            const variationInput = this.rootEl?.__fcVariationInputController;
            const variationId = sidecarMove.getAttribute('data-sidecar-variation-id');
            const moveIndex = parseInt(sidecarMove.getAttribute('data-sidecar-move-index'), 10);
            if (variationInput && typeof variationInput.loadVariationMove === 'function') {
                variationInput.loadVariationMove(variationId, moveIndex, true);
            }
            return;
        }

        // Regular path move click
        const moveEl = e.target.closest('[data-ply-index]');
        if (!moveEl || !this.replay) return;
        const plyIndex = parseInt(moveEl.getAttribute('data-ply-index'), 10);
        if (Number.isNaN(plyIndex)) return;
        const variationInput = this.rootEl?.__fcVariationInputController;
        if (variationInput && typeof variationInput.returnToMainline === 'function') {
            variationInput.returnToMainline(true);
        }
        this.replay.goTo(plyIndex, true, 'movelist');
    }

    // ─── Layout ────────────────────────────────────────────────────────────

    getNormalizedLayout() {
        return this.layout === 'vertical' ? 'vertical' : 'horizontal';
    }

    setLayout(layout) {
        this.layout = layout || 'horizontal';
        if (this.listEl) {
            this.listEl.setAttribute('data-layout', this.getNormalizedLayout());
        }
        if (this.replay) {
            const state = this.replay.getState();
            if (state && state.hasGame) {
                this.renderMoves(state);
                this.updateFromState(state);
            }
        }
    }

    // ─── Move rendering ────────────────────────────────────────────────────

    renderMoves(state) {
        if (!this.listEl) return;
        this._lastState = state ? { ...state } : null;
        const inner = this.listEl.querySelector('[data-role="moveListInner"]');
        if (!inner) return;
        const moves = Array.isArray(state?.moves) ? state.moves : [];
        if (!moves.length) {
            inner.innerHTML = '';
            // Don't hide the whole list — variation block may still need to show
            return;
        }
        const result = this.getGameResult(state);
        inner.innerHTML = this.getNormalizedLayout() === 'vertical'
            ? this.renderVerticalMoves(moves, result)
            : this.renderHorizontalMoves(moves, result);
        this.listEl.hidden = false;
    }

    renderHorizontalMoves(moves, result = '') {
        const rows = [];
        for (let i = 0; i < moves.length; i += 2) {
            const moveNo = Math.floor(i / 2) + 1;
            rows.push(`
                <div class="fc-boardViewer__moveRow" data-move-no="${moveNo}">
                    <span class="fc-boardViewer__moveNo">${moveNo}.</span>
                    ${this.renderMoveItem(moves[i] || '', i + 1, 'w')}
                    ${moves[i + 1]
                ? this.renderMoveItem(moves[i + 1], i + 2, 'b')
                : '<span class="fc-boardViewer__move fc-boardViewer__move--empty"></span>'}
                </div>
                ${i + 2 >= moves.length ? this.renderResultRow(result) : ''}
                ${this.renderSidecarVariationsForMoveNo(moveNo)}`);
        }
        return rows.join('');
    }

    renderVerticalMoves(moves, result = '') {
        const rows = [];
        for (let i = 0; i < moves.length; i += 2) {
            const moveNo = Math.floor(i / 2) + 1;
            rows.push(`
                <div class="fc-boardViewer__moveRow fc-boardViewer__moveRow--vertical" data-move-no="${moveNo}">
                    <span class="fc-boardViewer__moveNo">${moveNo}.</span>
                    ${this.renderMoveItem(moves[i] || '', i + 1, 'w')}
                    ${moves[i + 1]
                ? this.renderMoveItem(moves[i + 1], i + 2, 'b')
                : '<span class="fc-boardViewer__move fc-boardViewer__move--empty"></span>'}
                </div>
                ${i + 2 >= moves.length ? this.renderResultRow(result) : ''}
                ${this.renderSidecarVariationsForMoveNo(moveNo)}`);
        }
        return rows.join('');
    }

    renderMoveItem(san, plyIndex, side) {
        const safeSan    = this.escapeHtml(san || '');
        const figurineSan = this.toFigurineSan(safeSan);
        const firstChar  = figurineSan.charAt(0);
        const isFigurine = ['♔', '♕', '♖', '♗', '♘'].includes(firstChar);
        const html       = isFigurine
            ? `<span class="fc-boardViewer__figurine">${firstChar}</span>${figurineSan.slice(1)}`
            : figurineSan;
        return `<button type="button"
            class="fc-boardViewer__move"
            data-ply-index="${plyIndex}"
            data-side="${side}"
            title="${safeSan}">${html}</button>`;
    }

    getGameResult(state) {
        const result = String(state?.meta?.result || '').trim();
        return ['1-0', '0-1', '1/2-1/2', '*'].includes(result) ? result : '';
    }

    renderResultRow(result) {
        if (!result) return '';
        return `<div class="fc-boardViewer__resultRow" data-role="gameResult">
            <span class="fc-boardViewer__moveResult">${this.escapeHtml(result)}</span>
        </div>`;
    }

    renderSidecarVariationsForMoveNo(moveNo) {
        const variations = this.getSidecarVariationsForMoveNo(moveNo);
        if (!variations.length) {
            return '';
        }

        return variations.map((variation) => {
            const parentPly = Number.isInteger(variation.parentPly) ? variation.parentPly : 0;
            const moves = Array.isArray(variation.moves) ? variation.moves : [];
            const sequence = moves
                .map((move, index) => this.renderSidecarVariationMove(move, parentPly + index + 1, variation.id, index))
                .filter(Boolean)
                .join(' ');
            if (!sequence) {
                return '';
            }
            return `
                <div class="fc-boardViewer__sideVariationRow"
                     data-sidecar-variation-id="${this.escapeHtml(variation.id || '')}"
                     data-sidecar-parent-ply="${parentPly}">
                    <span class="fc-boardViewer__sideVariationBody">${sequence}</span>
                </div>`;
        }).join('');
    }

    getSidecarVariationsForMoveNo(moveNo) {
        if (!this._variationController || typeof this._variationController.getAllVariations !== 'function') {
            return [];
        }

        return this._variationController.getAllVariations()
            .filter((variation) => this.getDisplayMoveNoForParentPly(variation.parentPly) === moveNo);
    }

    getDisplayMoveNoForParentPly(parentPly) {
        const ply = Number(parentPly);
        if (!Number.isInteger(ply) || ply <= 0) {
            return 1;
        }
        return Math.max(1, Math.ceil(ply / 2));
    }

    renderSidecarVariationMove(move = {}, ply, variationId = '', moveIndex = 0) {
        const san = String(move.san || '').trim();
        if (!san) {
            return '';
        }

        const safePly = Number.isInteger(ply) && ply > 0 ? ply : 1;
        const safeVariationId = this.escapeHtml(variationId || '');
        const safeMoveIndex = Number.isInteger(moveIndex) && moveIndex >= 0 ? moveIndex : 0;
        const moveNo = Math.ceil(safePly / 2);
        const prefix = safePly % 2 === 1 ? `${moveNo}.` : `${moveNo}...`;
        return `<button type="button"
            class="fc-boardViewer__sideVariationMove"
            data-sidecar-variation-id="${safeVariationId}"
            data-sidecar-move-index="${safeMoveIndex}"
            data-sidecar-ply="${safePly}">
            <span class="fc-boardViewer__sideVariationMoveNo">${this.escapeHtml(prefix)}</span>
            <span class="fc-boardViewer__sideVariationSan">${this.renderSanText(san)}</span>
        </button>`;
    }

    renderSanText(san) {
        const safeSan = this.escapeHtml(san || '');
        const figurineSan = this.toFigurineSan(safeSan);
        const firstChar = figurineSan.charAt(0);
        const isFigurine = ['♔', '♕', '♖', '♗', '♘'].includes(firstChar);
        return isFigurine
            ? `<span class="fc-boardViewer__figurine">${firstChar}</span>${figurineSan.slice(1)}`
            : figurineSan;
    }

    // ─── Variation block ───────────────────────────────────────────────────

    _renderVariations() {
        if (!this.listEl) return;
        const block = this.listEl.querySelector('[data-role="variationsBlock"]');
        if (!block) return;

        if (!this._variations.length) {
            block.innerHTML = '';
            block.hidden = true;
            return;
        }

        const items = this._variations.map((v, i) => {
            const san    = this.escapeHtml(v.san || '');
            const fig    = this.toFigurineSan(san);
            const first  = fig.charAt(0);
            const isFig  = ['♔', '♕', '♖', '♗', '♘'].includes(first);
            const html   = isFig
                ? `<span class="fc-boardViewer__figurine">${first}</span>${fig.slice(1)}`
                : fig;
            const games  = v.games > 0
                ? `<span class="fc-bv__varGames">${v.games.toLocaleString()}</span>`
                : '';
            return `<button type="button"
                class="fc-bv__varBtn"
                data-variation-index="${i}"
                title="${san}">
                <span class="fc-bv__varSan">${html}</span>${games}
            </button>`;
        }).join('');

        block.innerHTML = `
            <div class="fc-bv__varLabel">Next moves</div>
            <div class="fc-bv__varList">${items}</div>`;
        block.hidden = false;
        this.listEl.hidden = false;
    }

    // ─── State sync ────────────────────────────────────────────────────────

    updateFromState(state) {
        if (!this.listEl) return;
        if (state) {
            this._lastState = {
                ...(this._lastState || {}),
                ...state
            };
        }
        const hasGame       = !!state?.hasGame;
        const hasVariations = this._variations.length > 0;
        // Keep visible when there are variations even if no path moves yet
        this.listEl.hidden = !hasGame && !hasVariations;
        this.highlightCurrentMove(state?.currentIndex ?? 0, state?.source || '', state);
    }

    highlightCurrentMove(currentIndex, source = '', state = null) {
        if (!this.listEl) return;
        const statefulEls = this.listEl.querySelectorAll('.is-active, .is-past');
        statefulEls.forEach(el => el.classList.remove('is-active', 'is-past'));
        if (source === 'variation-exploration') {
            this.blurFocusedMainlineMove();
            this.highlightActiveVariationMove(state);
            return;
        }
        if (!currentIndex || currentIndex < 1) return;
        const allMoveEls = this.listEl.querySelectorAll('[data-ply-index]');
        allMoveEls.forEach(el => {
            const ply = parseInt(el.getAttribute('data-ply-index'), 10);
            if (ply < currentIndex) el.classList.add('is-past');
        });
        const currentEl = this.listEl.querySelector(`[data-ply-index="${currentIndex}"]`);
        if (!currentEl) return;
        currentEl.classList.add('is-active');
        if (source !== 'movelist') {
            currentEl.scrollIntoView({ block: 'nearest', inline: 'nearest' });
            if (currentIndex === allMoveEls.length) {
                this.listEl.querySelector('[data-role="gameResult"]')
                    ?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
            }
        }
    }

    blurFocusedMainlineMove() {
        const active = document.activeElement;
        if (active && this.listEl.contains(active) && active.matches?.('[data-ply-index]')) {
            active.blur();
        }
    }

    highlightActiveVariationMove(state = null) {
        const activeVariationId = state?.activeVariationId || '';
        const activeVariationPly = Number(state?.activeVariationPly);
        if (!activeVariationId || !Number.isInteger(activeVariationPly)) {
            return;
        }
        const selector = `[data-sidecar-variation-id="${this.escapeSelector(activeVariationId)}"][data-sidecar-move-index="${activeVariationPly}"]`;
        const activeEl = this.listEl.querySelector(selector);
        if (!activeEl) {
            return;
        }
        activeEl.classList.add('is-active');
        activeEl.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }

    escapeSelector(value) {
        if (window.CSS && typeof window.CSS.escape === 'function') {
            return window.CSS.escape(String(value || ''));
        }
        return String(value || '').replace(/["\\]/g, '\\$&');
    }

    // ─── Clear / destroy ───────────────────────────────────────────────────

    clear() {
        this._variations       = [];
        this._onVariationClick = null;
        this._lastState = null;
        if (!this.listEl) return;
        const inner = this.listEl.querySelector('[data-role="moveListInner"]');
        if (inner) inner.innerHTML = '';
        const block = this.listEl.querySelector('[data-role="variationsBlock"]');
        if (block) { block.innerHTML = ''; block.hidden = true; }
        this.listEl.hidden = true;
    }

    toFigurineSan(san) {
        if (!san) return '';
        if (san === 'O-O' || san === 'O-O-O') return san;
        const figurines = { K: '♔', Q: '♕', R: '♖', B: '♗', N: '♘' };
        const first = san.charAt(0);
        return figurines[first] ? figurines[first] + san.slice(1) : san;
    }

    escapeHtml(value) {
        return String(value ?? '')
            .replaceAll('&', '&amp;')
            .replaceAll('<', '&lt;')
            .replaceAll('>', '&gt;')
            .replaceAll('"', '&quot;')
            .replaceAll("'", '&#039;');
    }

    destroy() {
        if (this.listEl) this.listEl.removeEventListener('click', this.onClick);
        if (this._unsubscribeSidecarVariations) { this._unsubscribeSidecarVariations(); this._unsubscribeSidecarVariations = null; }
        if (this.unsubscribeGameLoaded)     { this.unsubscribeGameLoaded();     this.unsubscribeGameLoaded     = null; }
        if (this.unsubscribePositionChange) { this.unsubscribePositionChange(); this.unsubscribePositionChange = null; }
        if (this.unsubscribeCleared)        { this.unsubscribeCleared();        this.unsubscribeCleared        = null; }
    }
}
